/* eslint-disable react-hooks/exhaustive-deps */
import { KeyboardEvent, MutableRefObject, useRef } from "react";
import { atomFamily, useSetRecoilState } from "recoil";

/**
 * エンターフォーカス
 */

// TODO: タブキーでのブラウザコンポーネント移動を防ぐ。
// TODO: ダイアログとメイン画面でフォーカスが被る

/**
 * useRefで保持するデータ
 */
type Focus = {
  // フォーカス位置
  index: number;
  // フォーカス対象要素
  element: any;
};

/**
 * Recoilで保持する関数群
 */
export type FocusState = {
  // フォーカス登録
  register: Function;
  // ref登録
  focusRef: Function;
  // 位置指定フォーカス
  focus: Function;
  // エンターキー入力フォーカス
  move: Function;
};

const defaultFocusState = {
  register: () => {},
  focusRef: () => {},
  focus: () => {},
  move: () => {},
};

const compare = (a: Focus, b: Focus) => {
  if (a.index > b.index) return 1;
  if (a.index < b.index) return -1;
  return 0;
};

const enabled = (f: Focus) => {
  if (f.element.disabled) {
    return !f.element.disabled;
  }
  if (f.element.props && f.element.props.disabled) {
    return !f.element.props.disabled;
  }
  return true;
};

/**
 * フォーカス関数群
 * @param ref
 * @returns
 */
const initFocus = (ref: MutableRefObject<Focus[]>, id: string) => {
  return {
    register: (index: number) => {
      return {
        index: index,
        focusId: id,
      };
    },
    focusRef: (index: number | undefined, element: HTMLElement) => {
      const focuses = ref.current;
      if (typeof index === "undefined" || index === 0) return;
      if (element === null) return;
      ref.current = focuses
        .filter((v) => v.index !== index)
        .concat({ index, element })
        .sort(compare);
    },
    focus: (index: number) => {
      const focuses = ref.current.filter((v) => enabled(v));
      const size = focuses.length;
      if (size <= 0) return;
      const element = focuses.filter((v) => v.index === index)[0].element;
      element.focus({ preventScroll: true });
      if ("select" in element) element.select();
      if ("scrollIntoView" in element) element.scrollIntoView();
    },
    move: (index: number | undefined, event: KeyboardEvent) => {
      const focuses = ref.current.filter((v) => enabled(v));
      const size = focuses.length - 1;
      if (typeof index === "undefined") return;
      if (size <= 0) return;
      if (event.key !== "Enter") return;
      let cur = focuses.findIndex((v) => v.index === index);
      if (event.shiftKey) {
        cur = cur <= 0 ? size : cur - 1;
      } else {
        cur = cur >= size ? 0 : cur + 1;
      }
      const element = focuses[cur].element;
      element.focus({ preventScroll: true });
      if ("select" in element) element.select();
      if ("scrollIntoView" in element) element.scrollIntoView();
    },
  };
};

/**
 * フォーカス初期化
 *
 * 位置指定フォーカス関数を返却する
 *
 * @returns
 */
export const useFocus = (id: string) => {
  const setFocus = useSetRecoilState(focusState(id));
  const ref = useRef<Focus[]>([]);
  const methods = initFocus(ref, id);
  setFocus(methods);
};

/**
 * フォーカスのメソッド提供ステート
 *
 */
export const focusState = atomFamily<FocusState, string>({
  key: "focusState",
  default: defaultFocusState,
});
